<template>
	<div
		v-if="$slots.title || $slots.actions"
		ref="route"
		class="route"
		:class="[`is-layout-${layout}`, { 'is-sticky': sticky }]"
	>
		<div v-if="$slots.title" ref="title" class="route-title" tabindex="-1">
			<slot name="title" />
		</div>

		<div v-if="$slots.actions" class="route-actions">
			<slot name="actions" />
		</div>
	</div>

	<slot />
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, useTemplateRef, watch } from 'vue'

import { type BreadcrumbItem } from '@/shared/global/AppBreadcrumbs.vue'
import { useBreadcrumbsStore } from '@/shared/stores/breadcrumbs.ts'
import { useDocumentTitlesStore } from '@/shared/stores/document-titles.ts'
import { announce } from '@/utilities/assistiveTechnology.ts'

const breadcrumbsStore = useBreadcrumbsStore()
const documentTitlesStore = useDocumentTitlesStore()

const {
	breadcrumbs = [],
	documentTitle,
	layout = 'row-wrap',
	sticky,
} = defineProps<{
	breadcrumbs?: BreadcrumbItem[]
	documentTitle?: string
	layout?: 'row' | 'row-wrap'
	sticky?: boolean
}>()

/** The unique key for this component instance's set of breadcrumbs. */
const key = Symbol()

const titleRef = useTemplateRef('title')
const routeRef = useTemplateRef('route')

/**
 * Publish the sticky header's height so elements further down the page can
 * stack their own sticky offsets below it (see `--sticky-inset-block-start`).
 *
 * The height cannot be expressed in CSS because it depends on how the title
 * and actions wrap at the current viewport size.
 */
let resizeObserver: ResizeObserver | undefined

watch(
	() => breadcrumbs,
	() => {
		if (breadcrumbs.length) {
			breadcrumbsStore.map.set(key, breadcrumbs)
		}
	},
	{ immediate: true },
)

watch(
	() => documentTitle,
	() => {
		if (documentTitle) {
			documentTitlesStore.map.set(key, documentTitle)
		}
	},
	{ immediate: true },
)

watch(
	documentTitlesStore.map,
	(titlesMap) => {
		const title = [...titlesMap.values()].toReversed().join(' – ')
		document.title = title
		announce('assertive', title)
	},
	{ immediate: true },
)

onMounted(() => {
	titleRef.value?.focus()

	if (!sticky || !routeRef.value) {
		return
	}

	resizeObserver = new ResizeObserver(([entry]) => {
		document.documentElement.style.setProperty(
			'--route-block-size',
			`${entry.borderBoxSize[0].blockSize}px`,
		)
	})

	resizeObserver.observe(routeRef.value)
})

onBeforeUnmount(() => {
	breadcrumbsStore.map.delete(key)

	resizeObserver?.disconnect()
	document.documentElement.style.removeProperty('--route-block-size')

	if (documentTitle) {
		documentTitlesStore.map.delete(key)
	}
})
</script>

<style scoped>
.route {
	border-block-end: var(--size-1) solid var(--c-grey-300);
	display: flex;
	gap: var(--size-16);
	margin-block-end: var(--size-24);
	padding-block-end: var(--size-16);
}

.route-title {
	outline: none;
}

/**
 * Add block start margin only if page has breadcrumbs.
 *
 * The margin is added to the route title instead of the route container so buttons
 * can go in the actions slot without changing the overall dimensions of the component.
 */
:where(html:has(.breadcrumbs)) .route-title {
	margin-block-start: 1.75rem;
}

.route-actions {
	display: flex;
	flex-wrap: wrap;
	gap: var(--size-8);
}

/* Row layout */

.route.is-layout-row {
	align-items: end;

	.route-title {
		flex-grow: 1;
	}

	.route-actions {
		justify-content: end;
	}
}

/* Row wrap layout */

.route.is-layout-row-wrap {
	@media (width < 80rem) {
		flex-direction: column;
	}

	@media (width >= 80rem) {
		align-items: end;

		.route-title {
			flex-grow: 1;
		}

		.route-actions {
			justify-content: end;
		}
	}
}

/* Sticky */

@media (height >= 50rem) {
	.route.is-sticky {
		background: var(--c-white);
		inset-block-start: var(--header-block-size);
		position: sticky;
		z-index: var(--z-index-route);
	}
}

/* Print */

@media print {
	.route {
		border: none !important;
		padding: 0 !important;

		&.is-sticky {
			position: unset !important;
		}
	}

	.route-title {
		margin-block-start: 0 !important;
	}

	.route-actions {
		display: none !important;
	}
}
</style>
